import { useCallback, useEffect, useRef, useState } from "react";
import { getRemoteStartStatus, startCharging } from "../services/apiService";
import analyticsService from "../services/analyticsService";

// Sans démarrage détecté dans ce délai, la recharge « ne se lance pas » (écran 1h).
// Le délai couvre aussi le branchement tardif : la borne qui a accepté l'ordre
// attend que le câble soit branché avant d'ouvrir la transaction.
export const START_TIMEOUT_MS = 2 * 60 * 1000;
export const START_STATUS_POLL_MS = 2000;

export const START_FAILURE = {
  // start-charge a répondu en erreur : borne déconnectée, client refusé, panne.
  REQUEST: "request",
  // La borne a refusé l'ordre de démarrage.
  REJECTED: "rejected",
  // Aucune transaction ouverte dans le délai.
  TIMEOUT: "timeout",
};

// Démarrage d'une recharge, de la demande jusqu'à l'ouverture de la transaction.
//
// Le démarrage est asynchrone côté QoREST : start-charge rend la main dès que
// l'ordre est parti et renvoie un `requestId`, dont on suit le statut pour
// connaître la réponse de la borne. Les bornes V1 (BSS) n'ont pas de `requestId` :
// seul le délai permet alors de conclure à un échec.
//
// La transaction elle-même est détectée par le suivi de session du composant
// (ongoing-use), qui le signale via `chargeStarted`. Ce suivi continue après un
// échec : une borne qui démarre en retard bascule donc quand même en recharge.
export default function useChargeStart({
  enabled,
  evcpReference,
  connectorReference,
  accessToken,
  chargeStarted,
}) {
  const [status, setStatus] = useState(enabled ? "connecting" : "idle");
  const [failureReason, setFailureReason] = useState(null);
  const [attempt, setAttempt] = useState(enabled ? 1 : 0);

  // Paramètres lus au moment de l'envoi : un nouveau rendu ne doit jamais
  // renvoyer l'ordre de démarrage, seule une nouvelle tentative le fait.
  const paramsRef = useRef({});
  paramsRef.current = { evcpReference, connectorReference, accessToken };
  const chargeStartedRef = useRef(chargeStarted);
  chargeStartedRef.current = chargeStarted;
  const stopAttemptRef = useRef(() => {});
  const attemptRef = useRef(attempt);
  attemptRef.current = attempt;

  useEffect(() => {
    if (!attempt || chargeStartedRef.current) return undefined;

    const { evcpReference: evcp, connectorReference: connector, accessToken: token } =
      paramsRef.current;
    const analyticsParams = { station_id: evcp, connector_id: connector, attempt };
    let stopped = false;
    let statusTimer = null;
    let timeoutTimer = null;

    const stop = () => {
      stopped = true;
      clearTimeout(timeoutTimer);
      clearTimeout(statusTimer);
    };

    const fail = (reason, error) => {
      if (stopped) return;
      stop();
      setFailureReason(reason);
      setStatus("failed");
      analyticsService.pushEvent("charge_start_failed", {
        ...analyticsParams,
        error_type: reason,
        ...(error?.status ? { http_status: error.status } : {}),
      });
    };

    timeoutTimer = setTimeout(() => fail(START_FAILURE.TIMEOUT), START_TIMEOUT_MS);

    const pollStatus = async (requestId) => {
      try {
        const data = await getRemoteStartStatus(requestId, token);
        if (stopped) return;

        // QoREST écrit "PENDING" à l'envoi, le service de transaction écrit la
        // réponse de la borne : on ne présume pas de la casse.
        const startStatus = String(data?.status || "").toLowerCase();
        if (startStatus === "rejected") {
          fail(START_FAILURE.REJECTED);
          return;
        }
        // Ordre accepté : il ne reste qu'à attendre la transaction.
        if (startStatus === "accepted") return;
      } catch (error) {
        if (stopped) return;
        // Statut expiré ou inconnu : plus rien à lire, le délai tranchera.
        if (error?.status === 404) return;
        // Toute autre erreur est traitée comme passagère.
      }

      statusTimer = setTimeout(() => pollStatus(requestId), START_STATUS_POLL_MS);
    };

    analyticsService.pushEvent("charge_start_requested", analyticsParams);

    startCharging(evcp, connector, token)
      .then((response) => {
        if (!stopped && response?.requestId) {
          pollStatus(response.requestId);
        }
      })
      .catch((error) => fail(START_FAILURE.REQUEST, error));

    stopAttemptRef.current = stop;
    return stop;
  }, [attempt]);

  useEffect(() => {
    if (!chargeStarted) return;

    stopAttemptRef.current();
    // Seul un démarrage demandé ici compte comme « charge_started » : une
    // session reprise était déjà en cours.
    if (attemptRef.current > 0) {
      analyticsService.pushEvent("charge_started", {
        station_id: paramsRef.current.evcpReference,
        connector_id: paramsRef.current.connectorReference,
      });
    }
    setFailureReason(null);
    setStatus("started");
  }, [chargeStarted]);

  // « Réessayer le démarrage » (écran 1h) : on renvoie l'ordre avec la même
  // préautorisation, qui reste valable tant qu'elle n'est ni capturée ni annulée.
  const retry = useCallback(() => {
    if (chargeStartedRef.current) return;
    setFailureReason(null);
    setStatus("connecting");
    setAttempt((current) => current + 1);
  }, []);

  return { status, failureReason, retry };
}
